import {
    createContext,
    useContext,
    useState,
    useEffect,
    useCallback,
} from "react";
import PropTypes from "prop-types";
import { cartApi, deliveryApi, getGuestId } from "../api";
import { validateCoupon as validateCouponApi } from "../api/coupon";
import { useAuth } from "./AuthContext";

const CartContext = createContext(undefined);

export function CartProvider({ children }) {
    const [cart, setCart] = useState(null);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showMiniCart, setShowMiniCart] = useState(false);

    // Operation-specific loading states
    const [addingToCart, setAddingToCart] = useState(false);
    const [updatingItemId, setUpdatingItemId] = useState(null);
    const [removingItemId, setRemovingItemId] = useState(null);
    const [clearingCart, setClearingCart] = useState(false);
    const [validating, setValidating] = useState(false);

    // Validation state
    const [validationResults, setValidationResults] = useState(null);
    const [validationWarnings, setValidationWarnings] = useState([]);

    // Delivery options state
    const [deliveryData, setDeliveryData] = useState(null);
    const [loadingDeliveryOptions, setLoadingDeliveryOptions] = useState(false);
    const [selectedDeliveryType, setSelectedDeliveryType] = useState(null);
    const [itemDeliveryTypes, setItemDeliveryTypes] = useState({}); // For per-item delivery when no common options

    // Coupon state - persisted in localStorage
    const [appliedCoupon, setAppliedCouponState] = useState(() => {
        try {
            const saved = localStorage.getItem("appliedCoupon");
            return saved ? JSON.parse(saved) : null;
        } catch {
            return null;
        }
    });
    const [couponLoading, setCouponLoading] = useState(false);
    const [couponError, setCouponError] = useState(null);

    // Get auth context safely (may be null if outside AuthProvider)
    let authContext = null;
    try {
        authContext = useAuth();
    } catch (e) {
        // Auth context not available
    }
    const isAuthenticated = authContext?.isAuthenticated || false;

    // Ensure guest ID exists
    useEffect(() => {
        getGuestId();
    }, []);

    // Fetch cart from backend
    const fetchCart = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await cartApi.getCart();

            if (response.success) {
                setCart(response.data);
                setItems(response.data?.items || []);
            } else {
                setError(response.message);
            }
        } catch (err) {
            console.error("Error fetching cart:", err);
            setError(err.response?.data?.message || "Failed to load cart");
            // On error, try to use local storage as fallback
            const savedCart = localStorage.getItem("cart");
            if (savedCart) {
                try {
                    setItems(JSON.parse(savedCart));
                } catch (e) {
                    console.error("Error loading cart from localStorage:", e);
                }
            }
        } finally {
            setLoading(false);
        }
    }, []);

    // Load cart on mount and when auth status changes
    useEffect(() => {
        fetchCart();
    }, [fetchCart, isAuthenticated]);

    // Sync cart when user logs in
    const syncCartOnLogin = async () => {
        const savedCart = localStorage.getItem("cart");
        if (savedCart) {
            try {
                const localItems = JSON.parse(savedCart);
                if (localItems.length > 0) {
                    const response = await cartApi.syncCart(localItems);
                    if (response.success) {
                        setCart(response.data);
                        setItems(response.data?.items || []);
                        localStorage.removeItem("cart"); // Clear local storage after sync
                    }
                }
            } catch (err) {
                console.error("Error syncing cart:", err);
            }
        }
        await fetchCart();
    };

    // Calculate totals from items
    const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
    const totalPrice =
        cart?.subtotal ||
        items.reduce((sum, item) => {
            const itemPrice = item.unitPrice || item.price || 0;
            return sum + itemPrice * item.quantity;
        }, 0);

    const getTotalPrice = () => totalPrice;

    // Add product to cart
    const addToCart = async (
        product,
        quantity = 1,
        size,
        color,
        customization = null,
        customizationGroupId = null
    ) => {
        setError(null);
        setAddingToCart(true);

        try {
            // Handle deal/bundle items
            if (product.type === "deal") {
                // Extract customization fields for deals
                const dealCustomization =
                    product.customization || customization;
                const hasCustomization = !!dealCustomization;

                // PRICING MODEL:
                // - customizationCharge = per-item position cost (gets multiplied by quantity in backend)
                // - setupFee is stored in customizationData.pricing.setupFee (one-time, does NOT scale)
                // - Frontend adds setupFee separately when displaying totals
                const pricing = dealCustomization?.pricing || {};
                const multiplier = product.multiplier || product.quantity || 1;
                // Per-item position cost = positionTotal / quantity
                const positionCostPerItem =
                    (pricing.positionTotal || 0) / multiplier;

                const dealData = {
                    dealId: product.dealId,
                    selectedProducts: product.selections, // Backend expects 'selectedProducts'
                    multiplier, // Handle both field names
                    hasCustomization,
                    customizationData: dealCustomization,
                    customizationCharge: positionCostPerItem, // Per-item position cost only
                    customizationGroupId, // Pass customization group ID
                    addLogoLater: dealCustomization?.addLogoLater || false,
                };

                const response = await cartApi.addDeal(dealData);

                if (response.success) {
                    setCart(response.data.cart);
                    setItems(response.data.cart?.items || []);
                    return { success: true };
                } else {
                    setError(response.message);
                    return { success: false, message: response.message };
                }
            }

            // Handle regular product items
            // Extract customization fields properly
            const hasCustomization = !!customization;
            const customizationData = customization || null;
            const customizationImageUrl =
                customization?.content?.imageUrl || null;
            const addLogoLater = customization?.addLogoLater || false;

            // PRICING MODEL:
            // - customizationCharge = per-item position cost (gets multiplied by quantity in backend)
            // - setupFee is stored in customizationData.pricing.setupFee (one-time, does NOT scale)
            // - Frontend adds setupFee separately when displaying totals
            const pricing = customization?.pricing || {};
            // Per-item position cost = positionTotal / quantity
            const positionCostPerItem = (pricing.positionTotal || 0) / quantity;

            const productData = {
                productId: product.id || product.productId,
                variantId: product.variantId || null,
                quantity,
                colorId: color?.id || color,
                sizeId: size?.id || size,
                hasCustomization,
                customizationData,
                customizationCharge: positionCostPerItem, // Per-item position cost only
                customizationImageUrl,
                customizationGroupId, // Pass customization group ID
                addLogoLater, // Add logo later flag
            };

            const response = await cartApi.addProduct(productData);

            if (response.success) {
                setCart(response.data.cart);
                setItems(response.data.cart?.items || []);
                return { success: true };
            } else {
                setError(response.message);
                return { success: false, message: response.message };
            }
        } catch (err) {
            console.error("Error adding to cart:", err);
            const errorMessage =
                err.response?.data?.message || "Failed to add to cart";
            setError(errorMessage);

            // Fallback to local storage
            const cartItem = {
                id: `${product.id}-${Date.now()}`,
                productId: product.id,
                type: product.type || "product",
                name: product.name,
                image: product.image,
                price: product.price,
                quantity,
                size,
                color,
                customization,
                customizationGroupId,
            };

            setItems((prevItems) => {
                const newItems = [...prevItems, cartItem];
                localStorage.setItem("cart", JSON.stringify(newItems));
                return newItems;
            });

            return { success: true, offline: true };
        } finally {
            setAddingToCart(false);
        }
    };

    // Remove item from cart
    const removeItem = async (itemId) => {
        setError(null);
        setRemovingItemId(itemId);

        try {
            const response = await cartApi.removeItem(itemId);

            if (response.success) {
                setCart(response.data.cart);
                setItems(response.data.cart?.items || []);
                return { success: true };
            } else {
                setError(response.message);
                return { success: false, message: response.message };
            }
        } catch (err) {
            console.error("Error removing from cart:", err);
            const errorMessage =
                err.response?.data?.message || "Failed to remove from cart";
            setError(errorMessage);

            // Fallback to local update
            setItems((prevItems) => {
                const newItems = prevItems.filter((item) => item.id !== itemId);
                localStorage.setItem("cart", JSON.stringify(newItems));
                return newItems;
            });

            return { success: true, offline: true };
        } finally {
            setRemovingItemId(null);
        }
    };

    // Update item quantity (or multiplier for deals)
    const updateQuantity = async (itemId, quantity, isDeal = false) => {
        if (quantity <= 0) {
            return removeItem(itemId);
        }

        setError(null);
        setUpdatingItemId(itemId);

        try {
            // For deals, send multiplier; for products, send quantity
            const updateData = isDeal ? { multiplier: quantity } : { quantity };
            const response = await cartApi.updateItem(itemId, updateData);

            if (response.success) {
                setCart(response.data.cart);
                setItems(response.data.cart?.items || []);
                return { success: true };
            } else {
                setError(response.message);
                return { success: false, message: response.message };
            }
        } catch (err) {
            console.error("Error updating cart item:", err);
            const errorMessage =
                err.response?.data?.message || "Failed to update cart";
            setError(errorMessage);

            // Fallback to local update
            setItems((prevItems) => {
                const newItems = prevItems.map((item) => {
                    if (item.id === itemId) {
                        return isDeal
                            ? { ...item, multiplier: quantity }
                            : { ...item, quantity };
                    }
                    return item;
                });
                localStorage.setItem("cart", JSON.stringify(newItems));
                return newItems;
            });

            return { success: true, offline: true };
        } finally {
            setUpdatingItemId(null);
        }
    };

    // Update cart item with any data (more flexible than updateQuantity)
    const updateCartItem = async (itemId, updateData) => {
        setError(null);
        setUpdatingItemId(itemId);

        try {
            const response = await cartApi.updateItem(itemId, updateData);

            if (response.success) {
                setCart(response.data.cart);
                setItems(response.data.cart?.items || []);
                return { success: true };
            } else {
                setError(response.message);
                return { success: false, message: response.message };
            }
        } catch (err) {
            console.error("Error updating cart item:", err);
            const errorMessage =
                err.response?.data?.message || "Failed to update cart";
            setError(errorMessage);
            return { success: false, message: errorMessage };
        } finally {
            setUpdatingItemId(null);
        }
    };

    // Update customization group (update all items with same customizationGroupId)
    const updateCustomizationGroup = async (groupId, updateData) => {
        setError(null);
        setUpdatingItemId(groupId); // Use groupId as identifier for loading state

        try {
            const response = await cartApi.updateCustomizationGroup(
                groupId,
                updateData
            );

            if (response.success) {
                setCart(response.data.cart);
                setItems(response.data.cart?.items || []);
                return {
                    success: true,
                    updatedCount: response.data.updatedCount,
                };
            } else {
                setError(response.message);
                return { success: false, message: response.message };
            }
        } catch (err) {
            console.error("Error updating customization group:", err);
            const errorMessage =
                err.response?.data?.message ||
                "Failed to update customization group";
            setError(errorMessage);
            return { success: false, message: errorMessage };
        } finally {
            setUpdatingItemId(null);
        }
    };

    // ============= COUPON MANAGEMENT =============

    // Helper to persist coupon to localStorage
    const setAppliedCoupon = useCallback((couponData) => {
        setAppliedCouponState(couponData);
        if (couponData) {
            localStorage.setItem("appliedCoupon", JSON.stringify(couponData));
        } else {
            localStorage.removeItem("appliedCoupon");
        }
    }, []);

    // Apply coupon code
    const applyCoupon = useCallback(
        async (code, subtotal) => {
            setCouponLoading(true);
            setCouponError(null);

            try {
                const response = await validateCouponApi(code, subtotal);

                if (response.success) {
                    const couponData = {
                        id:
                            response.data.couponId ||
                            response.data.userCouponId,
                        couponId: response.data.couponId,
                        userCouponId: response.data.userCouponId,
                        isUserCoupon: !!response.data.isUserCoupon,
                        code: response.data.code,
                        title: response.data.title,
                        discountAmount: response.data.discountAmount,
                        discountPercentage: response.data.discountPercentage,
                        maxDiscountAmount: response.data.maxDiscountAmount,
                        termsAndConditions: response.data.termsAndConditions,
                    };
                    setAppliedCoupon(couponData);
                    return { success: true, data: couponData };
                } else {
                    setCouponError(response.message);
                    return { success: false, message: response.message };
                }
            } catch (err) {
                const errorMessage =
                    err.response?.data?.message || "Failed to validate coupon";
                setCouponError(errorMessage);
                return { success: false, message: errorMessage };
            } finally {
                setCouponLoading(false);
            }
        },
        [setAppliedCoupon]
    );

    // Remove applied coupon
    const removeCoupon = useCallback(() => {
        setAppliedCoupon(null);
        setCouponError(null);
    }, [setAppliedCoupon]);

    // Clear coupon when cart is cleared
    const clearCoupon = useCallback(() => {
        setAppliedCoupon(null);
        setCouponError(null);
    }, [setAppliedCoupon]);

    // Clear cart
    const clearCart = async () => {
        setError(null);
        setClearingCart(true);

        try {
            const response = await cartApi.clearCart();

            if (response.success) {
                setCart(null);
                setItems([]);
                localStorage.removeItem("cart");
                clearCoupon(); // Also clear the applied coupon
                return { success: true };
            } else {
                setError(response.message);
                return { success: false, message: response.message };
            }
        } catch (err) {
            console.error("Error clearing cart:", err);
            const errorMessage =
                err.response?.data?.message || "Failed to clear cart";
            setError(errorMessage);

            // Fallback to local clear
            setItems([]);
            localStorage.removeItem("cart");

            return { success: true, offline: true };
        } finally {
            setClearingCart(false);
        }
    };

    // Validate cart before checkout
    const validateCart = async (autoFix = false) => {
        setValidating(true);
        setValidationResults(null);
        setValidationWarnings([]);

        try {
            const response = await cartApi.validateCart(autoFix);

            if (response.success) {
                const {
                    isValid,
                    validationResults: results,
                    warnings,
                    cart: validatedCart,
                } = response.data;

                setValidationResults(results);
                setValidationWarnings(warnings || []);

                // Update cart with validated/fixed data
                if (validatedCart) {
                    setCart(validatedCart);
                    setItems(validatedCart?.items || []);
                }

                return {
                    success: true,
                    isValid,
                    validationResults: results,
                    warnings: warnings || [],
                    itemsRemoved: response.data.itemsRemoved || 0,
                    itemsUpdated: response.data.itemsUpdated || 0,
                };
            } else {
                return {
                    success: false,
                    message: response.message,
                };
            }
        } catch (err) {
            console.error("Error validating cart:", err);
            return {
                success: false,
                message:
                    err.response?.data?.message || "Failed to validate cart",
            };
        } finally {
            setValidating(false);
        }
    };

    // Clear validation state
    const clearValidation = () => {
        setValidationResults(null);
        setValidationWarnings([]);
    };

    // Refresh cart
    const refreshCart = () => {
        fetchCart();
    };

    // Fetch delivery options for cart items
    const fetchDeliveryOptions = useCallback(async () => {
        // Get product IDs from cart items (only product items, not deals for now)
        const productIds = items
            .filter((item) => item.itemType === "PRODUCT" && item.product?.id)
            .map((item) => item.product.id);

        if (productIds.length === 0) {
            setDeliveryData(null);
            setSelectedDeliveryType(null);
            setItemDeliveryTypes({});
            return;
        }

        setLoadingDeliveryOptions(true);
        try {
            const response = await deliveryApi.getCommonDeliveryTypes(
                productIds
            );
            if (response.success) {
                // Check if products have different delivery option counts
                // Even if there's a "common" option, if some products have more options than others,
                // we should show individual selection
                const productDeliveryTypes =
                    response.data.productDeliveryTypes || {};
                const optionCounts = Object.values(productDeliveryTypes).map(
                    (opts) => opts?.length || 0
                );
                const hasVaryingOptions =
                    optionCounts.length > 0 &&
                    (new Set(optionCounts).size > 1 ||
                        optionCounts.some((count) => count > 1));

                // Override hasCommon if products have varying options
                const effectiveHasCommon =
                    response.data.hasCommon && !hasVaryingOptions;

                // Store the modified data
                const modifiedData = {
                    ...response.data,
                    hasCommon: effectiveHasCommon,
                    originalHasCommon: response.data.hasCommon, // Keep original for reference
                };

                setDeliveryData(modifiedData);

                // If there are common delivery types and no varying options, select the first one as default
                if (
                    effectiveHasCommon &&
                    response.data.commonDeliveryTypes.length > 0
                ) {
                    // Find default or use first
                    const defaultOption =
                        response.data.commonDeliveryTypes.find(
                            (dt) => dt.isDefault
                        ) || response.data.commonDeliveryTypes[0];
                    setSelectedDeliveryType(defaultOption.id);
                    setItemDeliveryTypes({});
                } else {
                    // No common options or varying options - need per-item selection
                    setSelectedDeliveryType(null);
                    // Initialize per-item delivery types with defaults
                    const perItemDefaults = {};
                    Object.entries(
                        response.data.productDeliveryTypes || {}
                    ).forEach(([productId, options]) => {
                        if (options.length > 0) {
                            const defaultOption =
                                options.find((opt) => opt.isDefault) ||
                                options[0];
                            perItemDefaults[productId] =
                                defaultOption.deliveryType?.id ||
                                defaultOption.id;
                        }
                    });
                    setItemDeliveryTypes(perItemDefaults);
                }
            }
        } catch (err) {
            console.error("Error fetching delivery options:", err);
            setDeliveryData(null);
        } finally {
            setLoadingDeliveryOptions(false);
        }
    }, [items]);

    // Fetch delivery options when cart items change
    useEffect(() => {
        if (items.length > 0 && !loading) {
            fetchDeliveryOptions();
        } else if (items.length === 0) {
            setDeliveryData(null);
            setSelectedDeliveryType(null);
            setItemDeliveryTypes({});
        }
    }, [items.length, loading]);

    // Handle global delivery type selection
    const handleSelectDeliveryType = (deliveryTypeId) => {
        setSelectedDeliveryType(deliveryTypeId);
    };

    // Handle per-item delivery type selection
    const handleSelectItemDeliveryType = (productId, deliveryTypeId) => {
        setItemDeliveryTypes((prev) => ({
            ...prev,
            [productId]: deliveryTypeId,
        }));
    };

    // Calculate delivery fee based on selection
    const calculateDeliveryFee = useCallback(
        (subtotal = 0) => {
            if (!deliveryData) return 0;

            if (deliveryData.hasCommon && selectedDeliveryType) {
                // Global delivery type selected
                const selectedOption = deliveryData.commonDeliveryTypes.find(
                    (dt) => dt.id === selectedDeliveryType
                );
                if (!selectedOption) return 0;

                if (!selectedOption.isChargeable) return 0;

                // Check free delivery threshold
                if (
                    selectedOption.freeDeliveryMinOrder &&
                    subtotal >= selectedOption.freeDeliveryMinOrder
                ) {
                    return 0;
                }

                return selectedOption.deliveryCharge || 0;
            } else if (Object.keys(itemDeliveryTypes).length > 0) {
                // Per-item delivery - sum up all delivery charges (or use highest, depending on business logic)
                // Using highest charge approach - only charge once for the most expensive delivery
                let maxCharge = 0;
                let allFree = true;

                Object.entries(itemDeliveryTypes).forEach(
                    ([productId, deliveryTypeId]) => {
                        const productOptions =
                            deliveryData.productDeliveryTypes?.[productId] ||
                            [];
                        // Handle both nested (opt.deliveryType) and flat structures
                        const selectedOption = productOptions.find((opt) => {
                            const dtId = opt.deliveryType?.id || opt.id;
                            return dtId === deliveryTypeId;
                        });

                        if (!selectedOption) return;

                        // Get delivery type info (handle both structures)
                        const deliveryType =
                            selectedOption.deliveryType || selectedOption;
                        const effectiveCharge =
                            selectedOption.priceOverride ??
                            deliveryType.deliveryCharge ??
                            0;
                        const isChargeable = deliveryType.isChargeable;
                        const freeThreshold = deliveryType.freeDeliveryMinOrder;

                        if (!isChargeable) return;

                        // Check free delivery threshold
                        if (freeThreshold && subtotal >= freeThreshold) {
                            return; // This item's delivery is free
                        }

                        allFree = false;
                        if (effectiveCharge > maxCharge) {
                            maxCharge = effectiveCharge;
                        }
                    }
                );

                return maxCharge;
            }

            return 0;
        },
        [deliveryData, selectedDeliveryType, itemDeliveryTypes]
    );

    // Get selected delivery info for display/checkout
    const getSelectedDeliveryInfo = useCallback(() => {
        if (!deliveryData) return null;

        if (deliveryData.hasCommon && selectedDeliveryType) {
            return {
                mode: "global",
                deliveryTypeId: selectedDeliveryType,
                deliveryType: deliveryData.commonDeliveryTypes.find(
                    (dt) => dt.id === selectedDeliveryType
                ),
            };
        } else if (Object.keys(itemDeliveryTypes).length > 0) {
            // Build detailed per-item delivery info
            const itemDeliveryDetails = {};
            Object.entries(itemDeliveryTypes).forEach(
                ([productId, deliveryTypeId]) => {
                    const productOptions =
                        deliveryData.productDeliveryTypes?.[productId] || [];
                    const selectedOption = productOptions.find((opt) => {
                        const dtId = opt.deliveryType?.id || opt.id;
                        return dtId === deliveryTypeId;
                    });
                    if (selectedOption) {
                        const deliveryType =
                            selectedOption.deliveryType || selectedOption;
                        itemDeliveryDetails[productId] = {
                            deliveryTypeId,
                            deliveryType,
                            priceOverride: selectedOption.priceOverride,
                            effectiveCharge:
                                selectedOption.priceOverride ??
                                deliveryType.deliveryCharge ??
                                0,
                        };
                    }
                }
            );

            return {
                mode: "per-item",
                itemDeliveryTypes,
                itemDeliveryDetails,
                deliveryTypes: deliveryData.productDeliveryTypes,
            };
        }

        return null;
    }, [deliveryData, selectedDeliveryType, itemDeliveryTypes]);

    return (
        <CartContext.Provider
            value={{
                cart,
                items,
                totalItems,
                totalPrice,
                loading,
                error,
                // Operation loading states
                addingToCart,
                updatingItemId,
                removingItemId,
                clearingCart,
                validating,
                // Validation state
                validationResults,
                validationWarnings,
                // Delivery options state
                deliveryData,
                loadingDeliveryOptions,
                selectedDeliveryType,
                itemDeliveryTypes,
                // Coupon state
                appliedCoupon,
                couponLoading,
                couponError,
                // Actions
                addToCart,
                removeItem,
                updateQuantity,
                updateCartItem,
                updateCustomizationGroup,
                clearCart,
                getTotalPrice,
                validateCart,
                clearValidation,
                refreshCart,
                syncCartOnLogin,
                showMiniCart,
                setShowMiniCart,
                // Coupon actions
                applyCoupon,
                removeCoupon,
                setAppliedCoupon,
                // Delivery actions
                fetchDeliveryOptions,
                handleSelectDeliveryType,
                handleSelectItemDeliveryType,
                calculateDeliveryFee,
                getSelectedDeliveryInfo,
            }}
        >
            {children}
        </CartContext.Provider>
    );
}

export function useCart() {
    const context = useContext(CartContext);
    if (context === undefined) {
        throw new Error("useCart must be used within a CartProvider");
    }
    return context;
}

CartProvider.propTypes = {
    children: PropTypes.node.isRequired,
};
